import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { X, Upload, CheckCircle, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { apiBaseUrl } from "../../services/config";

const API = apiBaseUrl;
const ax = () => ({ headers: { Authorization: `Bearer ${
  sessionStorage.getItem("super_token") ||
  JSON.parse(sessionStorage.getItem("platform_user")||"{}").access_token ||
  sessionStorage.getItem("access_token") || ""
}` } });

/* ═══════════════════════════════════════════════════════════════════
   COMPANY DATA MIGRATION MODAL — Super Admin
   Uploads the 6-sheet migration workbook (Departments, Designations,
   Employees, Leave Types, Salary Assignments) and submits it as ONE
   payload to the backend, scoped to a single company, in a single call.

   IMPORTANT: Employee Code is read verbatim from the sheet and sent
   as-is. It is NEVER generated here — the client's biometric device
   already punches attendance against these exact codes, so whatever
   is in the "Employee Code" column is what gets stored.
═══════════════════════════════════════════════════════════════════ */

const card = { background:"#0D1B2E", border:"1px solid rgba(0,200,255,0.15)", borderRadius:14 };
const inp = { width:"100%", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(0,200,255,0.15)",
  borderRadius:8, color:"#E8F0FE", fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif" };

const REQUIRED_EMPLOYEE_FIELDS = ["Employee Code", "First Name", "Last Name", "Email"];

const clean = (v) => (v === undefined || v === null) ? "" : String(v).trim();

function parseSheet(wb, name) {
  if (!wb.SheetNames.includes(name)) return [];
  const ws = wb.Sheets[name];
  const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
  // Header cells for required columns are written as "Employee Code *"
  // etc. (the asterisk marks them as required in the template). Strip
  // that suffix here so every lookup below (r["Employee Code"], etc.)
  // matches regardless of whether the uploaded sheet's headers include
  // the asterisk or not.
  const normalized = json.map(row => {
    const out = {};
    Object.entries(row).forEach(([k, v]) => {
      const cleanKey = String(k).replace(/\s*\*\s*$/, "").trim();
      out[cleanKey] = v;
    });
    return out;
  });
  return normalized.filter(row => Object.values(row).some(v => clean(v) !== ""));
}

export default function CompanyMigrationModal({ company, onClose, onSuccess }) {
  const [step, setStep] = useState(1);           // 1 upload, 2 preview, 3 submitting/result
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [parseError, setParseError] = useState("");
  const [parsed, setParsed] = useState(null);     // { departments, designations, employees, leaveTypes, salaryAssignments }
  const [empErrors, setEmpErrors] = useState([]); // rows skipped for missing required fields
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const fileRef = useRef();

  const parseFile = (f) => {
    setParseError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary", cellDates: true });

        const departments = parseSheet(wb, "Departments").map(r => ({
          name: clean(r["Department Name"]),
          description: clean(r["Description"]) || null,
        })).filter(d => d.name);

        const designations = parseSheet(wb, "Designations").map(r => ({
          title: clean(r["Designation Title"]),
          level: clean(r["Level"]) || null,
        })).filter(d => d.title);

        const rawEmployees = parseSheet(wb, "Employees");
        const employees = [];
        const errs = [];
        rawEmployees.forEach((r, idx) => {
          const missing = REQUIRED_EMPLOYEE_FIELDS.filter(f => !clean(r[f]));
          if (missing.length) {
            errs.push({ row: idx + 2, missing });
            return;
          }
          employees.push({
            // Employee Code is passed through EXACTLY as typed — never generated here.
            employee_code: clean(r["Employee Code"]),
            first_name: clean(r["First Name"]),
            last_name: clean(r["Last Name"]),
            email: clean(r["Email"]),
            phone: clean(r["Phone"]) || null,
            gender: clean(r["Gender"]) || null,
            date_of_birth: clean(r["Date of Birth"]) || null,
            marital_status: clean(r["Marital Status"]) || null,
            department: clean(r["Department"]) || null,
            designation: clean(r["Designation"]) || null,
            employment_type: clean(r["Employment Type"]) || "Full Time",
            date_of_joining: clean(r["Date of Joining"]) || null,
            branch_location: clean(r["Branch / Location"]) || null,
            status: clean(r["Status"]) || "Active",
            manager_employee_code: clean(r["Manager Employee Code"]) || null,
            emergency_contact: clean(r["Emergency Contact"]) || null,
            address_line1: clean(r["Address Line 1"]) || null,
            city: clean(r["City"]) || null,
            state: clean(r["State"]) || null,
            country: clean(r["Country"]) || "India",
            pin_code: clean(r["Pin Code"]) || null,
            pan_number: clean(r["PAN Number"]) || null,
            aadhar_number: clean(r["Aadhar Number"]) || null,
            uan_number: clean(r["UAN Number"]) || null,
            pf_number: clean(r["PF Number"]) || null,
            bank_name: clean(r["Bank Name"]) || null,
            bank_account_number: clean(r["Bank Account Number"]) || null,
            bank_ifsc: clean(r["Bank IFSC"]) || null,
            bank_branch: clean(r["Bank Branch"]) || null,
          });
        });

        const leaveTypes = parseSheet(wb, "Leave Types").map(r => ({
          name: clean(r["Leave Type Name"]),
          days_per_year: parseInt(clean(r["Days Per Year"])) || 0,
          carry_forward: clean(r["Carry Forward (Y/N)"]).toUpperCase() === "Y",
          max_carry: parseInt(clean(r["Max Carry"])) || 0,
          color: clean(r["Colour (hex)"]) || "#00C8FF",
          description: clean(r["Description"]) || null,
        })).filter(lt => lt.name);

        const salaryAssignments = parseSheet(wb, "Salary Assignments").map(r => ({
          employee_code: clean(r["Employee Code"]),
          structure_name: clean(r["Salary Structure Name"]),
          ctc: parseFloat(clean(r["Annual CTC"])) || 0,
          effective_from: clean(r["Effective From"]) || null,
        })).filter(s => s.employee_code && s.structure_name && s.ctc > 0);

        setParsed({ departments, designations, employees, leaveTypes, salaryAssignments });
        setEmpErrors(errs);
        setStep(2);
      } catch (err) {
        setParseError("Could not read this file. Please use the Hirality migration template (.xlsx).");
      }
    };
    reader.readAsBinaryString(f);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); parseFile(f); }
  };
  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); parseFile(f); }
  };

  const submitMigration = async () => {
    setStep(3); setSubmitting(true); setSubmitError(""); setResult(null);
    try {
      // Single call, scoped to this company — the backend performs the
      // dependency-ordered import (departments -> designations ->
      // employees -> manager linking -> leave types -> salary
      // assignments) and is idempotent: safe to re-run if it partially
      // fails, since it matches on employee_code / email / name rather
      // than blind-inserting.
      const res = await axios.post(
        `${API}/companies/${company.id}/migrate`,
        parsed,
        ax()
      );
      setResult(res.data || { message: "Migration submitted." });
    } catch (e) {
      setSubmitError(e.response?.data?.detail || e.message || "Migration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const totalRows = parsed
    ? parsed.departments.length + parsed.designations.length + parsed.employees.length +
      parsed.leaveTypes.length + parsed.salaryAssignments.length
    : 0;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(6,16,31,0.92)",
      backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div style={{ ...card, width:"100%", maxWidth:720, maxHeight:"90vh", overflow:"hidden",
        display:"flex", flexDirection:"column", fontFamily:"Outfit,sans-serif", color:"#E8F0FE" }}>

        {/* Header */}
        <div style={{ padding:"18px 24px", borderBottom:"1px solid rgba(0,200,255,0.1)",
          display:"flex", justifyContent:"space-between", alignItems:"center",
          background:"linear-gradient(135deg,rgba(0,200,255,0.08),rgba(124,92,255,0.08))" }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800 }}>📦 Migrate Company Data</div>
            <div style={{ fontSize:12, color:"#7A93B4", marginTop:2 }}>{company.name}</div>
          </div>
          <button onClick={onClose} disabled={submitting}
            style={{ background:"none", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"50%",
              width:30, height:30, color:"#7A93B4", cursor: submitting ? "not-allowed" : "pointer" }}>
            <X size={14}/>
          </button>
        </div>

        <div style={{ padding:24, overflowY:"auto", flex:1 }}>

          {/* ── STEP 1: UPLOAD ── */}
          {step === 1 && (
            <div>
              <div style={{ background:"rgba(0,200,255,0.06)", border:"1px solid rgba(0,200,255,0.15)",
                borderRadius:10, padding:"12px 16px", marginBottom:18, fontSize:12.5, color:"#7A93B4", lineHeight:1.7 }}>
                Upload the Hirality migration workbook for <strong style={{ color:"#00C8FF" }}>{company.name}</strong>.
                Departments, Designations, Employees, Leave Types and Salary Assignments are all read from one file.
                <br/><strong style={{ color:"#FFB800" }}>Employee Code is required for every employee row</strong> and
                is imported exactly as typed — this must match the code used on their biometric device.
              </div>

              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current.click()}
                style={{
                  border:`2px dashed ${dragOver ? "#00C8FF" : "rgba(0,200,255,0.2)"}`,
                  borderRadius:14, padding:"44px 20px", textAlign:"center", cursor:"pointer",
                  background: dragOver ? "rgba(0,200,255,0.05)" : "transparent", transition:"all .2s",
                }}>
                <FileSpreadsheet size={36} style={{ color:"#00C8FF", marginBottom:10 }}/>
                <div style={{ fontSize:15, fontWeight:700, marginBottom:5 }}>Drop the migration workbook here</div>
                <div style={{ fontSize:12.5, color:"#7A93B4", marginBottom:14 }}>or click to browse · .xlsx</div>
                <button style={{ padding:"9px 20px", borderRadius:8, border:"none",
                  background:"linear-gradient(135deg,#00C8FF,#7C5CFF)", color:"#fff", fontWeight:700,
                  fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                  Choose File
                </button>
                {file && <div style={{ marginTop:12, fontSize:12.5, color:"#00E5A3", fontWeight:600 }}>✓ {file.name}</div>}
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFileInput} style={{ display:"none" }}/>

              {parseError && (
                <div style={{ marginTop:14, padding:"10px 14px", borderRadius:8,
                  background:"rgba(255,77,109,0.1)", border:"1px solid rgba(255,77,109,0.3)",
                  color:"#FF4D6D", fontSize:12.5, fontWeight:600 }}>
                  ⚠️ {parseError}
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: PREVIEW ── */}
          {step === 2 && parsed && (
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:18 }}>
                {[
                  { label:"Departments", val:parsed.departments.length, color:"#00C8FF" },
                  { label:"Designations", val:parsed.designations.length, color:"#7C5CFF" },
                  { label:"Employees", val:parsed.employees.length, color:"#00E5A3" },
                  { label:"Leave Types", val:parsed.leaveTypes.length, color:"#FFB800" },
                  { label:"Salary Assignments", val:parsed.salaryAssignments.length, color:"#00E5A3" },
                  { label:"Total Rows", val:totalRows, color:"#E8F0FE" },
                ].map(s => (
                  <div key={s.label} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)",
                    borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontSize:22, fontWeight:900, fontFamily:"JetBrains Mono,monospace", color:s.color }}>{s.val}</div>
                    <div style={{ fontSize:11, color:"#7A93B4", marginTop:2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {empErrors.length > 0 && (
                <div style={{ marginBottom:18, padding:"12px 16px", borderRadius:10,
                  background:"rgba(255,77,109,0.08)", border:"1px solid rgba(255,77,109,0.25)" }}>
                  <div style={{ fontSize:12.5, fontWeight:700, color:"#FF4D6D", marginBottom:6,
                    display:"flex", alignItems:"center", gap:6 }}>
                    <AlertTriangle size={13}/> {empErrors.length} employee row{empErrors.length!==1?"s":""} skipped — missing required field(s)
                  </div>
                  <div style={{ maxHeight:100, overflowY:"auto" }}>
                    {empErrors.slice(0, 8).map((e, i) => (
                      <div key={i} style={{ fontSize:11.5, color:"#7A93B4", marginBottom:3 }}>
                        Row {e.row}: missing {e.missing.join(", ")}
                      </div>
                    ))}
                    {empErrors.length > 8 && (
                      <div style={{ fontSize:11.5, color:"#7A93B4" }}>...and {empErrors.length - 8} more</div>
                    )}
                  </div>
                  <div style={{ fontSize:11.5, color:"#FF9500", marginTop:8 }}>
                    These rows will NOT be imported. Fix the file and re-upload if you need them included —
                    Employee Code especially can't be filled in automatically since it must match the biometric device.
                  </div>
                </div>
              )}

              {parsed.employees.length === 0 && (
                <div style={{ marginBottom:18, padding:"12px 16px", borderRadius:10,
                  background:"rgba(255,184,0,0.08)", border:"1px solid rgba(255,184,0,0.25)",
                  fontSize:12.5, color:"#FFB800" }}>
                  No valid employee rows found. Check the Employees sheet has Employee Code, First Name,
                  Last Name and Email filled in for at least one row.
                </div>
              )}

              <div style={{ background:"rgba(0,200,255,0.05)", border:"1px solid rgba(0,200,255,0.15)",
                borderRadius:10, padding:"12px 16px", fontSize:12.5, color:"#7A93B4", lineHeight:1.7 }}>
                This will be sent as a single migration request for <strong style={{ color:"#00C8FF" }}>{company.name}</strong>.
                The import is safe to re-run — existing records (matched by email / employee code / name) are
                skipped rather than duplicated, so if something fails partway through you can fix the sheet and
                upload again without creating duplicates.
              </div>
            </div>
          )}

          {/* ── STEP 3: SUBMIT / RESULT ── */}
          {step === 3 && (
            <div style={{ textAlign:"center", padding:"20px 0" }}>
              {submitting && (
                <>
                  <div style={{ width:44, height:44, border:"4px solid rgba(0,200,255,0.15)",
                    borderTopColor:"#00C8FF", borderRadius:"50%", margin:"0 auto 18px",
                    animation:"spin 0.9s linear infinite" }}/>
                  <div style={{ fontSize:15, fontWeight:700 }}>Migrating {company.name}'s data…</div>
                  <div style={{ fontSize:12.5, color:"#7A93B4", marginTop:6 }}>
                    This can take a little while for {parsed?.employees.length || 0} employees. Don't close this window.
                  </div>
                </>
              )}

              {!submitting && submitError && (
                <>
                  <div style={{ fontSize:44, marginBottom:12 }}>⚠️</div>
                  <div style={{ fontSize:16, fontWeight:800, color:"#FF4D6D", marginBottom:8 }}>Migration Failed</div>
                  <div style={{ fontSize:12.5, color:"#7A93B4", background:"rgba(255,77,109,0.08)",
                    border:"1px solid rgba(255,77,109,0.25)", borderRadius:8, padding:"10px 14px",
                    display:"inline-block", maxWidth:480 }}>
                    {submitError}
                  </div>
                  <div style={{ fontSize:11.5, color:"#7A93B4", marginTop:12 }}>
                    Nothing that succeeded before the failure needs to be undone — re-running after a fix
                    will skip anything already created and only process what's left.
                  </div>
                </>
              )}

              {!submitting && result && !submitError && (
                <>
                  <div style={{ fontSize:44, marginBottom:12 }}>🎉</div>
                  <div style={{ fontSize:16, fontWeight:800, color:"#00E5A3", marginBottom:14 }}>Migration Submitted</div>
                  <pre style={{ textAlign:"left", fontSize:11.5, color:"#7A93B4", background:"rgba(255,255,255,0.03)",
                    border:"1px solid rgba(255,255,255,0.08)", borderRadius:8, padding:14, maxHeight:220,
                    overflow:"auto", whiteSpace:"pre-wrap" }}>
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 24px", borderTop:"1px solid rgba(0,200,255,0.1)",
          display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            {step === 2 && (
              <button onClick={() => { setStep(1); setFile(null); setParsed(null); }}
                style={{ padding:"8px 16px", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
                  background:"none", color:"#7A93B4", cursor:"pointer", fontFamily:"inherit", fontSize:13 }}>
                ← Choose different file
              </button>
            )}
          </div>
          <div style={{ display:"flex", gap:10 }}>
            {step === 2 && (
              <button onClick={submitMigration} disabled={!parsed || parsed.employees.length === 0}
                style={{ padding:"9px 22px", borderRadius:8, border:"none",
                  background: (!parsed || parsed.employees.length === 0) ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg,#00C8FF,#7C5CFF)",
                  color:"#fff", cursor: (!parsed || parsed.employees.length === 0) ? "not-allowed" : "pointer",
                  fontFamily:"inherit", fontWeight:700, fontSize:13 }}>
                🚀 Start Migration ({parsed?.employees.length || 0} employees)
              </button>
            )}
            {step === 3 && !submitting && (
              <button onClick={() => { onClose(); if (result && !submitError && onSuccess) onSuccess(); }}
                style={{ padding:"9px 22px", borderRadius:8, border:"none",
                  background:"linear-gradient(135deg,#00E5A3,#00C8FF)", color:"#06101F",
                  cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:13 }}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}